import {
  // eslint-disable-next-line import/named
  PayloadAction,
  createAsyncThunk,
  createSlice,
  isAnyOf,
} from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import {
  GetCountryLookUpType,
  InvoiceListQueryParams,
  InvoiceListState,
  InvoicesListTypes,
} from '../../../types/Invoicing/Invoices/InvoiceListTypes'
import { LoadingState, ValidationError } from '../../../types/commonTypes'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import invoiceListApi from '../../../middleware/api/Invoicing/Invoices/invoiceListApi'
import { RootState } from '../../../stateStore'
import {
  filterOptionStatusInfo,
  outgoingInitialGetInvoiceListProperties,
} from '../../../pages/Invoicing/Invoices/StatusFilterHelper'
import { RatingCheckEnum } from '../../../types/Performance/ReviewList/reviewListTypes'

const getInvoiceList = createAsyncThunk(
  'invoiceList/invoiceListType',
  async (queryParams: InvoiceListQueryParams, thunkApi) => {
    try {
      return await invoiceListApi.invoiceListType(queryParams)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getCountryList = createAsyncThunk(
  'invoiceList/getCountryList',
  async (_, thunkApi) => {
    try {
      return await invoiceListApi.getCountryList()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const selectDate = 'Select Date'
const projectTypeValue = 'ALL'
const invoiceTypeValue = 'INVOICE'

export const initialInvoiceListState: InvoiceListState = {
  getInvoiceList: {} as InvoicesListTypes,
  getCountryLookUpList: [],
  listSize: 0,
  isLoading: ApiLoadingState.idle,
  isCountryLoading: ApiLoadingState.idle,
  SelectValue: selectDate,
  SelectProjectType: projectTypeValue,
  SelectInvoiceType: invoiceTypeValue,
  StatusValue: '',
  SelectCountry: '',
  customFromValue: '',
  customToValue: '',
  multiText: '',
  OutgoingGetInvoiceListProperties: outgoingInitialGetInvoiceListProperties,
  checkedItems: ['SENT', 'OVER DUE', 'PARTIALLY RECEIVED'],
  pagination: 1,
  intrnalOrNot: false,
}
const invoiceListSlice = createSlice({
  name: 'invoiceList',
  initialState: initialInvoiceListState,
  reducers: {
    setSelectValue: (state, action) => {
      state.SelectValue = action.payload
    },
    setSelectProjectValue: (state, action) => {
      state.SelectProjectType = action.payload
    },
    setSelectInvoiceValue: (state, action) => {
      state.SelectInvoiceType = action.payload
    },
    setSelectStatusValue: (state, action) => {
      state.StatusValue = action.payload
    },
    setSelectCountryValue: (state, action) => {
      state.SelectCountry = action.payload
    },
    setSelectCustomFromValue: (state, action) => {
      state.customFromValue = action.payload
    },
    setSelectCustomToValue: (state, action) => {
      state.customToValue = action.payload
    },
    setSearchInputVal: (state, action) => {
      state.multiText = action.payload
    },
    setIsInternalValue: (state, action) => {
      state.intrnalOrNot = action.payload
    },
    setSelectCheckedItems: (state, action) => {
      state.checkedItems = action.payload
    },

    setStatusForGetInvoiceList: (
      state,
      action: PayloadAction<{
        type: RatingCheckEnum
        value: string
        indexVal: number
      }>,
    ) => {
      const { type, value, indexVal } = action.payload
      if (type === RatingCheckEnum.checkAll.toString()) {
        state.OutgoingGetInvoiceListProperties = {
          ...state.OutgoingGetInvoiceListProperties,
          status: filterOptionStatusInfo,
        }
      } else if (type === RatingCheckEnum.uncheckAll.toString()) {
        state.OutgoingGetInvoiceListProperties = {
          ...state.OutgoingGetInvoiceListProperties,
          status: [],
        }
      } else if (type === RatingCheckEnum.checkNum.toString()) {
        state.OutgoingGetInvoiceListProperties.status.push(value)
      } else {
        state.OutgoingGetInvoiceListProperties.status.splice(indexVal, 1)
      }
    },
  },

  extraReducers: (builder) => {
    builder.addCase(getInvoiceList.fulfilled, (state, action) => {
      state.getInvoiceList = action.payload
      state.listSize = action.payload.InvoiceListSize
      state.isLoading = ApiLoadingState.succeeded
    })
    builder.addCase(getCountryList.fulfilled, (state, action) => {
      state.getCountryLookUpList = action.payload
      state.isCountryLoading = ApiLoadingState.succeeded
    })
    builder.addMatcher(isAnyOf(getInvoiceList.pending), (state) => {
      state.isLoading = ApiLoadingState.loading
    })
    builder.addMatcher(isAnyOf(getCountryList.pending), (state) => {
      state.isCountryLoading = ApiLoadingState.loading
    })
  },
})
const invoiceListThunk = {
  getInvoiceList,
  getCountryList,
}
const isLoading = (state: RootState): LoadingState =>
  state.invoiceList.isLoading

const invoiceList = (state: RootState): InvoicesListTypes =>
  state.invoiceList.getInvoiceList

const countries = (state: RootState): GetCountryLookUpType[] =>
  state.invoiceList.getCountryLookUpList

const getSelectValue = (state: RootState): string =>
  state.invoiceList.SelectValue

const getProjectValue = (state: RootState): string =>
  state.invoiceList.SelectProjectType

const getInvoiceValue = (state: RootState): string =>
  state.invoiceList.SelectInvoiceType

const getStatusValue = (state: RootState): string =>
  state.invoiceList.StatusValue

const listSize = (state: RootState): number => state.invoiceList.listSize

const getCountryValue = (state: RootState): string =>
  state.invoiceList.SelectCountry

const getCustomFromValue = (state: RootState): string | Date | null =>
  state.invoiceList.customFromValue

const getCustomToValue = (state: RootState): string =>
  state.invoiceList.customToValue

const getSearchInputValue = (state: RootState): string | null =>
  state.invoiceList.multiText

const getIsInternalValue = (state: RootState): boolean =>
  state.invoiceList.intrnalOrNot

const getCheckedItems = (state: RootState): string[] =>
  state.invoiceList.checkedItems

const invoiceListSelectors = {
  isLoading,
  invoiceList,
  countries,
  getSelectValue,
  listSize,
  getProjectValue,
  getInvoiceValue,
  getStatusValue,
  getCountryValue,
  getCustomFromValue,
  getCustomToValue,
  getSearchInputValue,
  getCheckedItems,
  getIsInternalValue,
}
export const invoiceListService = {
  ...invoiceListThunk,
  actions: invoiceListSlice.actions,
  selectors: invoiceListSelectors,
}
const invoiceListReducer = invoiceListSlice.reducer

export default invoiceListReducer
